import React, { useState } from "react";
import { Dropdown } from "react-bootstrap";
import { FaSearch } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";

import { onSetSelectContactFields } from "../store/slices/contactSlice";
import { setSelectedProductFields } from "../store/slices/productSlice";
import { setSelectedInvoiceFields } from "../store/slices/invoiceSlice";

function SetupFieldsDropdown({
  fieldsData,
  dropdownFor,
  selectedPreference,
  isDisabled,
  selectedPipelines,
}) {
  const [searchInput, setSearchInput] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  const { customerNumberPreference } = useSelector((state) => state.contact);

  const dispatch = useDispatch();

  const searchFields = (enteredInput) => {
    setSearchInput(enteredInput);
    let searchArray;
    if (
      dropdownFor === "hubspotFilterFields" ||
      dropdownFor === "txFilterFields" ||
      dropdownFor === "hsCustomerNumberField" ||
      dropdownFor === "txCustomerNumberField" ||
      dropdownFor === "txProductFilterField" ||
      dropdownFor === "hubspotProductFilterField" ||
      dropdownFor === "products" ||
      dropdownFor === "defaultProduct"
    ) {
      searchArray = fieldsData.filter((fieldItem) =>
        fieldItem.HRF.toLowerCase().includes(enteredInput.toLowerCase())
      );
    } else {
      searchArray = fieldsData.filter((fieldItem) =>
        fieldItem.title.toLowerCase().includes(enteredInput.toLowerCase())
      );
    }
    setSearchResults(searchArray);
  };

  /**
   * @description This function is used for disabling the selected pipeline from the dropdown.
   */
  const disableSelectedPipelines = (value) => {
    return selectedPipelines.find((item) => item.pipeline.value === value);
  };

  const disabledCustomerItems = (fieldItem) => {
    if (
      customerNumberPreference.value === "hubspot" &&
      fieldItem.CRF === "doNotAdd"
    ) {
      return true;
    }
  };

  const findSyncFieldsTitle = (preferenceValue) => {
    if (
      dropdownFor === "hubspotFilterFields" ||
      dropdownFor === "hsCustomerNumberField" ||
      dropdownFor === "hubspotProductFilterField"
    ) {
      const field = fieldsData.find(
        (fieldItem) => fieldItem.CRF === preferenceValue
      );
      if (field) {
        return field.HRF;
      } else {
        return "custom property deleted";
      }
    } else if (
      dropdownFor === "txFilterFields" ||
      dropdownFor === "txCustomerNumberField" ||
      dropdownFor === "txProductFilterField" ||
      dropdownFor === "products" ||
      dropdownFor === "defaultProduct"
    ) {
      const field = fieldsData.find(
        (fieldItem) => fieldItem.CRF === preferenceValue
      );
      if (field) {
        return field.HRF;
      } else {
        return "property deleted";
      }
    } else {
      const field = fieldsData.find(
        (fieldItem) => fieldItem.value === preferenceValue
      );
      if (field) {
        if (field?.title === "") {
          return field.value;
        }
        return field.title;
      } else {
        return "property deleted";
      }
    }
  };

  const toggleDropdownText = () => {
    if (
      dropdownFor === "txFilterFields" ||
      dropdownFor === "txProductFilterField" ||
      dropdownFor === "txCustomerNumberField"
    ) {
      if (!selectedPreference) {
        return "Select Tripletex Field";
      } else {
        return findSyncFieldsTitle(selectedPreference.value);
      }
    } else if (
      dropdownFor === "hubspotFilterFields" ||
      dropdownFor === "hubspotProductFilterField" ||
      dropdownFor === "hsCustomerNumberField"
    ) {
      if (!selectedPreference) {
        return "Select HubSpot Field";
      } else {
        return findSyncFieldsTitle(selectedPreference.value) ===
          "property deleted"
          ? "custom property deleted"
          : findSyncFieldsTitle(selectedPreference.value);
      }
    } else if (
      dropdownFor === "currency" ||
      dropdownFor === "orderCurrency" ||
      dropdownFor === "productCurrency"
    ) {
      if (!selectedPreference) {
        return "Select currency";
      } else {
        return findSyncFieldsTitle(selectedPreference.value) ===
          "property deleted"
          ? "selected currency deleted"
          : findSyncFieldsTitle(selectedPreference.value);
      }
    } else if (dropdownFor === "language") {
      if (!selectedPreference) {
        return "Select Language";
      } else {
        return findSyncFieldsTitle(selectedPreference.value) ===
          "property deleted"
          ? "selected language deleted"
          : findSyncFieldsTitle(selectedPreference.value);
      }
    } else if (dropdownFor === "accountManager") {
      if (!selectedPreference) {
        return "Select Account Manager";
      } else {
        return findSyncFieldsTitle(selectedPreference.value) ===
          "property deleted"
          ? "account manager deleted"
          : findSyncFieldsTitle(selectedPreference.value);
      }
    } else if (
      dropdownFor === "department" ||
      dropdownFor === "orderDepartment" ||
      dropdownFor === "productDepartment"
    ) {
      if (!selectedPreference) {
        return "Select department";
      } else {
        return findSyncFieldsTitle(selectedPreference.value) ===
          "property deleted"
          ? "department deleted"
          : findSyncFieldsTitle(selectedPreference.value);
      }
    } else if (dropdownFor === "productUnit") {
      if (!selectedPreference) {
        return "Select unit";
      } else {
        return findSyncFieldsTitle(selectedPreference.value) ===
          "property deleted"
          ? "selected unit deleted"
          : findSyncFieldsTitle(selectedPreference.value);
      }
    } else if (dropdownFor === "productWeight") {
      if (!selectedPreference) {
        return "Select weight";
      } else {
        return findSyncFieldsTitle(selectedPreference.value) ===
          "property deleted"
          ? "selected weight deleted"
          : findSyncFieldsTitle(selectedPreference.value);
      }
    } else if (dropdownFor === "productVolume") {
      if (!selectedPreference) {
        return "Select volume";
      } else {
        return findSyncFieldsTitle(selectedPreference.value) ===
          "property deleted"
          ? "selected volume deleted"
          : findSyncFieldsTitle(selectedPreference.value);
      }
    } else if (dropdownFor === "products" || dropdownFor === "defaultProduct") {
      if (!selectedPreference) {
        return "Select product";
      } else {
        return findSyncFieldsTitle(selectedPreference.value) ===
          "property deleted"
          ? "selected product deleted"
          : findSyncFieldsTitle(selectedPreference.value);
      }
    } else if (dropdownFor === "pipelines") {
      if (!selectedPreference) {
        return "Select pipeline";
      } else {
        return findSyncFieldsTitle(selectedPreference.value) ===
          "property deleted"
          ? "selected pipeline deleted"
          : findSyncFieldsTitle(selectedPreference.value);
      }
    } else if (dropdownFor === "dealStages") {
      if (!selectedPreference) {
        return "Select deal stage";
      } else {
        return findSyncFieldsTitle(selectedPreference.value) ===
          "property deleted"
          ? "selected deal stage deleted"
          : findSyncFieldsTitle(selectedPreference.value);
      }
    } else if (dropdownFor === "preferenceForCreation") {
      if (!selectedPreference) {
        return "Select preference";
      } else {
        return findSyncFieldsTitle(selectedPreference.value) ===
          "property deleted"
          ? "selected preference deleted"
          : findSyncFieldsTitle(selectedPreference.value);
      }
    }
  };

  const onChangePreference = ({ dropdownFor, fieldItem }) => {
    if (
      dropdownFor === "pipelines" ||
      dropdownFor === "dealStages" ||
      dropdownFor === "preferenceForCreation" ||
      dropdownFor === "orderCurrency" ||
      dropdownFor === "orderDepartment"
    ) {
      dispatch(
        setSelectedInvoiceFields({
          selectingFor: dropdownFor,
          selectedOption: fieldItem,
        })
      );
    } else if (
      dropdownFor === "productCurrency" ||
      dropdownFor === "productDepartment" ||
      dropdownFor === "productWeight" ||
      dropdownFor === "productUnit" ||
      dropdownFor === "productVolume"
    ) {
      dispatch(
        setSelectedProductFields({
          selectingFor: dropdownFor,
          selectedOption: fieldItem,
        })
      );
    } else {
      dispatch(
        onSetSelectContactFields({
          selectingFor: dropdownFor,
          selectedOption: fieldItem,
        })
      );
    }
  };

  return (
    <Dropdown className={"dropdown "}>
      <Dropdown.Toggle
        disabled={isDisabled}
        className={
          toggleDropdownText() === "custom property deleted" ||
          toggleDropdownText() === "selected currency deleted" ||
          toggleDropdownText() === "property deleted" ||
          toggleDropdownText() === "selected language deleted" ||
          toggleDropdownText() === "account manager deleted" ||
          toggleDropdownText() === "department deleted" ||
          toggleDropdownText() === "selected unit deleted" ||
          toggleDropdownText() === "selected weight deleted" ||
          toggleDropdownText() === "selected volume deleted" ||
          toggleDropdownText() === "selected product deleted" ||
          toggleDropdownText() === "selected pipeline deleted" ||
          toggleDropdownText() === "selected deal stage deleted"
            ? "my-2 w-100 setup-fields-dropdown-color"
            : "my-2 w-100 setup-fields-dropdown"
        }
      >
        {toggleDropdownText()}
      </Dropdown.Toggle>

      <Dropdown.Menu
        renderOnMount={true}
        popperConfig={{ strategy: "fixed" }}
        className="dropdown-index"
      >
        <div className="p-3 dropdown-search-container">
          <div className="d-flex flex-row dropdown-search-box">
            <input
              type="search"
              placeholder="Search.."
              className="w-100 dropdown-search"
              onChange={(e) => searchFields(e.target.value)}
            />
            <span className="my-auto">
              <FaSearch size={15} className="me-2 search-icon" />
            </span>
          </div>
        </div>

        {dropdownFor === "hubspotFilterFields" ||
        dropdownFor === "txFilterFields" ||
        dropdownFor === "hsCustomerNumberField" ||
        dropdownFor === "txCustomerNumberField" ||
        dropdownFor === "txProductFilterField" ||
        dropdownFor === "hubspotProductFilterField" ||
        dropdownFor === "products" ||
        dropdownFor === "defaultProduct" ? (
          <div className="setup-fields-dropdown-items-container">
            {searchInput.length > 0 && searchResults.length === 0 && (
              <div className="d-flex flex-column justify-content-center align-items-center h-100">
                <p style={{ fontSize: "14px", fontWeight: 600 }}>
                  No results found
                </p>
              </div>
            )}
            {fieldsData.length === 0 && (
              <div className="d-flex flex-column justify-content-center align-items-center h-100">
                <p style={{ fontSize: "14px", fontWeight: 600 }}>
                  No items found
                </p>
              </div>
            )}
            {(searchInput.length > 0 ? searchResults : fieldsData).map(
              (fieldItem) => (
                <Dropdown.Item
                  key={fieldItem.CRF}
                  disabled={disabledCustomerItems(fieldItem)}
                  onClick={() => {
                    dropdownFor === "txProductFilterField" ||
                    dropdownFor === "hubspotProductFilterField" ||
                    dropdownFor === "products" ||
                    dropdownFor === "defaultProduct"
                      ? dispatch(
                          setSelectedProductFields({
                            selectingFor: dropdownFor,
                            selectedOption: fieldItem,
                          })
                        )
                      : dispatch(
                          onSetSelectContactFields({
                            selectingFor: dropdownFor,
                            selectedOption: fieldItem,
                          })
                        );
                  }}
                >
                  {fieldItem.HRF}
                </Dropdown.Item>
              )
            )}
          </div>
        ) : (
          <div className="setup-fields-dropdown-items-container">
            {searchInput.length > 0 && searchResults.length === 0 && (
              <div className="d-flex flex-column justify-content-center align-items-center h-100">
                <p style={{ fontSize: "14px", fontWeight: 600 }}>
                  No results found
                </p>
              </div>
            )}
            {fieldsData.length === 0 && (
              <div className="d-flex flex-column justify-content-center align-items-center h-100">
                <p style={{ fontSize: "14px", fontWeight: 600 }}>
                  No items found
                </p>
              </div>
            )}
            {(searchInput.length > 0 ? searchResults : fieldsData).map(
              (fieldItem) => (
                <Dropdown.Item
                  key={fieldItem.title}
                  disabled={
                    dropdownFor === "pipelines" &&
                    disableSelectedPipelines(fieldItem.value)
                  }
                  onClick={() => {
                    onChangePreference({ dropdownFor, fieldItem });
                  }}
                >
                  {fieldItem.title}
                </Dropdown.Item>
              )
            )}
          </div>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
}

export default SetupFieldsDropdown;
